import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupIcon from '@mui/icons-material/Group';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import BigBoardLogo, { pngBoxStyle } from '../logo/BigBoardLogo';

const features = [
  {
    icon: SportsFootballIcon,
    title: 'Make Your Picks',
    description: 'Pick the winners for every college football game each week of the season.',
  },
  {
    icon: EmojiEventsIcon,
    title: 'Predict the Score',
    description: 'Pick the winner and predict the final score for every game.',
  },
  {
    icon: GroupIcon,
    title: 'Leaderboard',
    description: 'Compete against friends and see who has the best picks all season long.',
  },
  {
    icon: NotificationsActiveIcon,
    title: 'Stay in the Loop',
    description: 'Get notified when new games are posted and before kickoff so you never miss a pick.',
  },
];

/* Neutral palette — no team colors */
const palette = {
  heroGradient: 'linear-gradient(170deg, #0a0a0a 0%, #1c1c1c 50%, #2a2a2a 100%)',
  ctaBackground: '#1c1c1c',
  featuresBg: '#f7f7f7',
  accent: '#d4d4d4',
  iconColor: '#555',
  cardBorder: '#e0e0e0',
  subtleText: 'rgba(255,255,255,0.7)',
};

export default function LandingPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Same footprint as the old 8em/5em PNG, which sat 2rem above the title.
  const heroLogoBox = pngBoxStyle(isMobile ? '5em' : '8em');

  return (
    <Box sx={{ backgroundColor: '#fff' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: palette.heroGradient,
          color: 'white',
          textAlign: 'center',
          py: isMobile ? 8 : 12,
          px: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.04) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <BigBoardLogo
            data-logo-target="hero"
            style={{
              ...heroLogoBox,
              marginBottom: `calc(${heroLogoBox.marginBottom} + 2rem)`,
              filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.3))',
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            sx={{
              fontWeight: 800,
              fontFamily: '"Georgia", "Times New Roman", serif',
              mb: 2,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            College Football Pick'em
          </Typography>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            sx={{
              mb: 5,
              color: palette.subtleText,
              maxWidth: 520,
              mx: 'auto',
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Pick the winners, predict the scores, and compete against
            friends all season long.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{
                fontWeight: 700,
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                backgroundColor: '#fff',
                color: '#1c1c1c',
                borderRadius: '4px',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#e8e8e8',
                  boxShadow: 'none',
                },
              }}
            >
              Sign Up
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                fontWeight: 700,
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.4)',
                borderRadius: '4px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              Log In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: isMobile ? 7 : 10, px: 2, backgroundColor: palette.featuresBg }}>
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              textAlign: 'center',
              mb: 1,
              letterSpacing: '0.2em',
              color: '#888',
              fontSize: '0.75rem',
            }}
          >
            How It Works
          </Typography>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{
              fontWeight: 800,
              fontFamily: '"Georgia", "Times New Roman", serif',
              textAlign: 'center',
              mb: 6,
              letterSpacing: '-0.01em',
            }}
          >
            Four steps to game day
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid key={feature.title} size={{ xs: 12, sm: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'left',
                    height: '100%',
                    borderRadius: '8px',
                    border: `1px solid ${palette.cardBorder}`,
                    backgroundColor: '#fff',
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                      borderColor: '#bbb',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.75rem',
                        color: '#aaa',
                        fontFamily: 'monospace',
                        mr: 1.5,
                      }}
                    >
                      0{index + 1}
                    </Typography>
                    <feature.icon sx={{ fontSize: 28, color: palette.iconColor }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontFamily: '"Georgia", "Times New Roman", serif',
                      mb: 1,
                      fontSize: '1.1rem',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Footer CTA Section */}
      <Box
        sx={{
          backgroundColor: palette.ctaBackground,
          color: 'white',
          textAlign: 'center',
          py: isMobile ? 7 : 9,
          px: 2,
        }}
      >
        <Container maxWidth="sm">
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{
              fontWeight: 800,
              fontFamily: '"Georgia", "Times New Roman", serif',
              mb: 2,
              letterSpacing: '-0.01em',
            }}
          >
            Ready to play?
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: palette.subtleText, lineHeight: 1.6 }}
          >
            Join The Big Board and start making your picks today.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            sx={{
              fontWeight: 700,
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              backgroundColor: '#fff',
              color: '#1c1c1c',
              borderRadius: '4px',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#e8e8e8',
                boxShadow: 'none',
              },
            }}
          >
            Get Started
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: '#111',
          color: 'rgba(255,255,255,0.5)',
          textAlign: 'center',
          py: 3,
          px: 2,
          fontSize: '0.8rem',
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
            <RouterLink to="/terms" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 'inherit', textDecoration: 'none' }}>
              Terms of Service
            </RouterLink>
            <span>|</span>
            <RouterLink to="/privacy" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 'inherit', textDecoration: 'none' }}>
              Privacy Policy
            </RouterLink>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            &copy; {new Date().getFullYear()} The Big Board
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
